import { useState, useRef } from "react"
import { useForm } from "react-hook-form"
import { PasswordRequirementsTooltip } from "@/modules/shared/ui/components/password-requirements-tooltip"
import { zodResolver } from "@hookform/resolvers/zod"
import * as z from "zod"
import { Button } from "@/modules/shared/ui/components/button"
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/modules/shared/ui/components/card"
import { Input } from "@/modules/shared/ui/components/input"
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/modules/shared/ui/components/form"
import { FaEye, FaEyeSlash } from "react-icons/fa"
import { Link } from "react-router-dom"

const formSchema = z
  .object({
    oldPassword: z.string().min(1, {
      message: "Please enter your current password",
    }),
    newPassword: z
      .string()
      .min(8, "Password must be at least 8 characters long")
      .regex(/[A-Z]/, "Password must contain at least one uppercase letter")
      .regex(/[a-z]/, "Password must contain at least one lwercase letter")
      .regex(/[0-9]/, "Password mist contain at least one number")
      .regex(
        /[!@#$%^&*]/,
        "Password must contain at least one special character (!@#$%^&*)",
      ),
    confirmPassword: z.string().min(1, {
      message: "Please confirm your password",
    }),
  })
  .refine((data) => data.newPassword === data.confirmPassword, {
    message: "Passwords do not match",
    path: ["confirmPassword"],
  })
  .refine((data) => data.oldPassword !== data.newPassword, {
    message: "New Password must be different from your current password",
    path: ["newPassword"],
  })

type FormValues = z.infer<typeof formSchema>

interface ChangePasswordFormProps {
  onSubmit: (oldPassword: string, newPassword: string) => Promise<void>
  isLoading?: boolean
  errorMessage?: string
  hasValidToken?: boolean
  isCoolingDown?: boolean
  email?: string
}

export default function ChangePasswordForm({
  onSubmit,
  isLoading = false,
  errorMessage = "",
  hasValidToken = true,
  isCoolingDown = false,
  email = "",
}: ChangePasswordFormProps) {
  const [showOldPassword, setShowOldPassword] = useState(false)
  const [showNewPassword, setShowNewPassword] = useState(false)
  const [showConfirmPassword, setShowConfirmPassword] = useState(false)

  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      oldPassword: "",
      newPassword: "",
      confirmPassword: "",
    },
  })
  const submittingRef = useRef(false)

  const handleSubmit = async (values: FormValues) => {
    if (submittingRef.current) return
    submittingRef.current = true
    try {
      await onSubmit(values.oldPassword, values.newPassword)
    } finally {
      submittingRef.current = false
    }
  }

  if (!hasValidToken) {
    return (
      <div className="w-full md:w-[75%] justify-center items-center">
        <Card className="shadow-none border-0 w-full">
          <CardHeader>
            <CardTitle className="text-3xl">
              Invalid Change-Password Link
            </CardTitle>
            <CardDescription className="text-base text-muted-foreground">
              This link is invalid or has expired.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <p className="text-muted-foreground">
                Please request a new link from your profile.
              </p>
              <Link
                to="/profile"
                className="inline-flex items-center justify-center px-6 py-3 text-base font-medium text-white rounded-md"
                style={{ backgroundColor: "#1A237E" }}
              >
                Go to profile
              </Link>
            </div>
          </CardContent>
        </Card>
      </div>
    )
  }

  return (
    <div className="w-full md:w-[75%] justify-center items-center">
      <Card className="shadow-none border-0 w-full">
        <CardHeader>
          <CardTitle className="text-3xl">Change your password</CardTitle>
          <CardDescription className="text-base text-muted-foreground">
            {email ? (
              <>
                Set a new password for{" "}
                <span className="font-medium text-foreground">{email}</span>.
              </>
            ) : (
              "Set a new password below."
            )}
          </CardDescription>
        </CardHeader>

        <CardContent>
          <Form {...form}>
            <form
              onSubmit={form.handleSubmit(handleSubmit)}
              className="space-y-5"
            >
              <FormField
                control={form.control}
                name="oldPassword"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="text-foreground font-medium">
                      Current Password
                    </FormLabel>
                    <FormControl>
                      <div className="relative">
                        <Input
                          type={showOldPassword ? "text" : "password"}
                          placeholder="Enter your current password"
                          className="pr-10 border-border focus:border-blue-500 focus:ring-blue-500"
                          {...field}
                        />
                        <button
                          type="button"
                          onClick={() => setShowOldPassword((prev) => !prev)}
                          className="absolute right-3 top-3 text-muted-foreground hover:text-foreground"
                        >
                          {showOldPassword ? <FaEyeSlash /> : <FaEye />}
                        </button>
                      </div>
                    </FormControl>
                    <FormMessage className="text-red-500" />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="newPassword"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="text-foreground font-medium flex items-center gap-1.5">
                      <span>New Password</span>
                      <PasswordRequirementsTooltip />
                    </FormLabel>
                    <FormControl>
                      <div className="relative">
                        <Input
                          type={showNewPassword ? "text" : "password"}
                          placeholder="Enter new password"
                          className="pr-10 border-border focus:border-blue-500 focus:ring-blue-500"
                          {...field}
                        />
                        <button
                          type="button"
                          onClick={() => setShowNewPassword((prev) => !prev)}
                          className="absolute right-3 top-3 text-muted-foreground hover:text-foreground"
                        >
                          {showNewPassword ? <FaEyeSlash /> : <FaEye />}
                        </button>
                      </div>
                    </FormControl>
                    <FormMessage className="text-red-500" />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="confirmPassword"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="text-foreground font-medium">
                      Confirm New Password
                    </FormLabel>
                    <FormControl>
                      <div className="relative">
                        <Input
                          type={showConfirmPassword ? "text" : "password"}
                          placeholder="Confirm new password"
                          className="pr-10 border-border focus:border-blue-500 focus:ring-blue-500"
                          {...field}
                        />
                        <button
                          type="button"
                          onClick={() =>
                            setShowConfirmPassword((prev) => !prev)
                          }
                          className="absolute right-3 top-3 text-muted-foreground hover:text-foreground"
                        >
                          {showConfirmPassword ? <FaEyeSlash /> : <FaEye />}
                        </button>
                      </div>
                    </FormControl>
                    <FormMessage className="text-red-500" />
                  </FormItem>
                )}
              />

              {errorMessage && (
                <p className="text-sm text-red-600 mt-1">{errorMessage}</p>
              )}

              <Button
                type="submit"
                className="w-full text-white py-6 text-base"
                style={{ backgroundColor: "#1A237E" }}
                disabled={isLoading || isCoolingDown}
              >
                {isCoolingDown
                  ? "Try again later"
                  : isLoading
                    ? "Changing..."
                    : "Change password"}
              </Button>

              <div className="text-center">
                <Link
                  to="/login"
                  className="text-sm font-medium hover:underline"
                  style={{ color: "#1A237E" }}
                >
                  Back to login
                </Link>
              </div>
            </form>
          </Form>
        </CardContent>
      </Card>
    </div>
  )
}
